import './index.scss';
import * as React from 'react';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { INPUT_TYPE_MAP, INPUT_VERIFY_MAP } from '../types';

import { REGS } from '../../../lib/utils/validators';

import { noop, preventSyntheticEvent } from '../../../lib/utils';
import DELETE from '../../../assets/images/delete.png';
import { EmptyData } from '../../../lib/components/EmptyData';

const validatorFactory = reg => str => reg.test(str);

function TextInput({
  data: {
    label,
    type: formType,
    verify,
    withMaxLength,
    maxLength,
    required,
    tip,
    defaultValue,
  },
  setFormValue: iSetFormValue,
  showVerifyInfo = false,
  subNode = null,
  afterNode = null,
  background = '#FFFFFF',
  formatter = i => i,
  warnText,
  scrollAfterBlur,
}) {
  const setFormValue = iSetFormValue || noop;
  const [value, setValue] = useState(defaultValue || '');
  const [focus, setFocus] = useState(false);
  const [type, setType] = useState(getType(formType));
  const [valid, setValid] = useState(true);
  const [isInputComposition, setIsInputComposition] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => setType(getType(formType)), [formType]);

  useEffect(() => {
    const valid = validator(value);
    setValid(valid);
    setFormValue({ key: label, value, valid });
  }, [verify]);

  const validator = useMemo(() => {
    if (typeof verify === 'function') {
      return verify;
    }

    switch (verify) {
      case INPUT_VERIFY_MAP.email:
        return validatorFactory(REGS.email);
      case INPUT_VERIFY_MAP.idCard:
        return validatorFactory(REGS.idCard);
      case INPUT_VERIFY_MAP.qq:
        return validatorFactory(REGS.qq);
      case INPUT_VERIFY_MAP.url:
        return validatorFactory(REGS.url);
      case INPUT_VERIFY_MAP.zip:
        return validatorFactory(REGS.zip);
      case INPUT_VERIFY_MAP.phone:
        return validatorFactory(REGS.phone);
    }
    return required ? str => !!str.toString().trim() : () => true;
  }, [verify, required]);

  const onChange = useCallback(
    ({ target: { value } }) => {
      setValue(value);
      const valid = validator(value);
      setValid(valid);

      setFormValue({ key: label, value, valid });
    },
    [setValue, validator]
  );

  const onInput = useCallback(
    ({ target }) => {
      if (isInputComposition) {
        return;
      }

      let value = formatter(target.value);
      if (withMaxLength && value.length > maxLength) {
        value = value.slice(0, maxLength);
      }

      target.value = value;
    },
    [withMaxLength, maxLength, type, isInputComposition]
  );

  const focusInput = useCallback(
    e => {
      inputRef.current.focus({ preventScroll: false });
      return preventSyntheticEvent(e);
    },
    [inputRef.current]
  );

  if (!iSetFormValue) {
    return <EmptyData text="该组件只能作为[辅导表单容器]的子组件" />;
  }

  const validatorInfo = getValidatorInfo();

  return (
    <div
      className={classnames('fudao-form-item fudao-text-input', {
        focus,
        empty: !value.trim(),
        invalid: warnText || !!validatorInfo,
      })}
      onClick={focusInput}
      // onTouchStart={focusInput}
    >
      <span
        className="fudao-text-input-label"
        style={{ backgroundColor: background }}
        onClick={focusInput}
        // onTouchStart={focusInput}
      >
        {label}
      </span>
      <input
        ref={inputRef}
        type={type}
        pattern={formType === INPUT_TYPE_MAP.number ? '\\d*' : ''}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        onFocus={() => setFocus(true)}
        onBlur={e => {
          onInput(e);
          onChange(e);
          setFocus(false);
          if (scrollAfterBlur) {
            setTimeout(() => {
              if (
                document.activeElement &&
                document.activeElement.nodeName.toUpperCase() === 'INPUT'
              ) {
                return;
              }
              window.scrollTo(0, 0);
            }, 500);
          }
        }}
        onInput={onInput}
        onCompositionStart={() => setIsInputComposition(true)}
        onCompositionEnd={() => setIsInputComposition(false)}
        onClick={focusInput}
        // onTouchStart={focusInput}
      />
      <div className="fudao-text-input-sub-node">
        <div
          onClick={e => {
            onChange({ target: { value: '' } });
            focusInput(e);
            return preventSyntheticEvent(e);
          }}
          className="fudao-text-input-delete"
          style={{ backgroundImage: `url(${DELETE})` }}
        />
        {subNode}
      </div>
      <div className="fudao-text-input-tip">{tip}</div>
      <div className="fudao-text-input-validator-info">
        {warnText || validatorInfo}
      </div>
      {withMaxLength ? (
        <div className="fudao-text-length-info">
          {value.toString().length}/{maxLength}
        </div>
      ) : null}
      {afterNode ? (
        <div className="fudao-text-after-node">{afterNode}</div>
      ) : null}
    </div>
  );

  function getValidatorInfo() {
    if (valid || !showVerifyInfo) {
      return null;
    }

    if (required && !value.toString().trim()) {
      return `${label}不能为空`;
    }

    return `${typeof verify === 'string' ? verify : label}格式不正确`;
  }
}

function getType(type) {
  if (type === INPUT_TYPE_MAP.number) {
    return 'number';
  }
  if (type === INPUT_TYPE_MAP.password) {
    return 'password';
  }
  return 'text';
}

export { TextInput };
